import React from "react";
import { EyeFilled } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { ImageMask } from "../../../../../components";
import { getFilterStyle } from "../../../../../helpers/filterStyles";
import { getRandomGif } from "../../../../../helpers/getRandomGif";
import { searchOpen } from "../../../../dashboard/store/modalSlice/modalSlice";

import "./style.css";

export const ImageListCard = ({ card }) => {
  const dispatch = useDispatch();
  const { url, name, rotation, mimeType, width, height, template, filter, giphyId, giphyUsername, source } = card;
  const isVideo = mimeType && mimeType.startsWith('video/');
  const validUrl = url && typeof url === 'string' && url.trim() !== '' ? url : getRandomGif();
  const [isHovered, setIsHovered] = React.useState(false);
  const [mask, setMask] = React.useState(false);
  const [dimensions, setDimensions] = React.useState(null);
  
  // Get template from card or default to 1up
  const currentTemplate = template || "1up";
  const currentFilter = filter || "";

  const maskOpen = () => {
    setMask(true);
  };

  const maskClose = () => {
    setMask(false);
    setIsHovered(false);
  };

  const calculateDimensions = (imgWidth, imgHeight, rot) => {
    if (!imgWidth || !imgHeight) return null;

    const r = rot ? parseInt(rot) : 0;
    const isRotated90or270 = Math.abs(r) === 90 || Math.abs(r) === 270;
    const effectiveWidth = isRotated90or270 ? imgHeight : imgWidth;
    const effectiveHeight = isRotated90or270 ? imgWidth : imgHeight;

    const aspectRatio = effectiveWidth / effectiveHeight;
    let orientation = "Square";
    if (aspectRatio < 0.9) {
      orientation = "Portrait";
    } else if (aspectRatio > 1.1) {
      orientation = "Landscape";
    }

    const ratio = aspectRatio.toFixed(2);
    return {
      orientation,
      ratio,
      width: effectiveWidth,
      height: effectiveHeight,
    };
  };

  const getOrientationClass = (imgWidth, imgHeight, rot) => {
    const r = rot ? parseInt(rot) : 0;
    const isRotated90or270 = Math.abs(r) === 90 || Math.abs(r) === 270;
    const effectiveWidth = isRotated90or270 ? imgHeight : imgWidth;
    const effectiveHeight = isRotated90or270 ? imgWidth : imgHeight;
    const aspectRatio = effectiveWidth / effectiveHeight;

    if (aspectRatio > 1.1) return 'image_list_card__img--landscape';
    if (aspectRatio < 0.9) return 'image_list_card__img--portrait';
    return 'image_list_card__img--square';
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const imgWidth = width || img.naturalWidth;
    const imgHeight = height || img.naturalHeight;

    if (imgWidth && imgHeight) {
      img.classList.remove('image_list_card__img--landscape', 'image_list_card__img--portrait', 'image_list_card__img--square');
      img.classList.add(getOrientationClass(imgWidth, imgHeight, rotation));
      setDimensions(calculateDimensions(imgWidth, imgHeight, rotation));
    }
  };

  const handleVideoLoad = (e) => {
    const video = e.target;
    const videoWidth = width || video.videoWidth;
    const videoHeight = height || video.videoHeight;

    if (videoWidth && videoHeight) {
      video.classList.remove('image_list_card__img--landscape', 'image_list_card__img--portrait', 'image_list_card__img--square');
      video.classList.add(getOrientationClass(videoWidth, videoHeight, rotation));
      setDimensions(calculateDimensions(videoWidth, videoHeight, rotation));
    }
  };

  // Recalculate dimensions when width, height, or rotation changes
  React.useEffect(() => {
    if (width && height) {
      setDimensions(calculateDimensions(width, height, rotation));
    }
  }, [width, height, rotation]);

  const renderMediaElement = (additionalClassName = "", additionalStyle = {}) => {
    const filterStyle = getFilterStyle(currentFilter);
    const baseStyle = { ...filterStyle, transform: `rotate(${rotation}deg)`, ...additionalStyle };
    const className = `image_list_card__template-item ${additionalClassName}`.trim();
    
    if (isVideo) {
      return (
        <video
          src={validUrl}
          className={className}
          style={baseStyle}
          onLoadedMetadata={handleVideoLoad}
          muted
          playsInline
        />
      );
    } else {
      return (
        <img
          src={validUrl}
          alt={name}
          className={className}
          style={baseStyle}
          onLoad={handleImageLoad}
          onError={(e) => {
            if (e.target.src !== getRandomGif()) {
              e.target.src = getRandomGif();
            }
          }}
        />
      );
    }
  };

  const renderTemplatePreview = () => {
    const baseClassName = "image_list_card__template";
    const templateClassName = `${baseClassName} ${baseClassName}--${currentTemplate}`;
    
    // Render based on template
    if (currentTemplate === "1up") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {renderMediaElement()}
        </div>
      );
    } else if (currentTemplate === "n2Up") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--left">
            {renderMediaElement()}
          </div>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--right">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "n2Up-mirror-left") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--left">
            {renderMediaElement("image_list_card__template-item--mirror-left")}
          </div>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--right">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "n2Up-mirror-right") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--left">
            {renderMediaElement()}
          </div>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--right">
            {renderMediaElement("image_list_card__template-item--mirror-right")}
          </div>
        </div>
      );
    } else if (currentTemplate === "4up") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="image_list_card__template-item-wrapper">
              {renderMediaElement()}
            </div>
          ))}
        </div>
      );
    } else if (currentTemplate === "4up-warhol") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="image_list_card__template-item-wrapper">
              {renderMediaElement(`image_list_card__template-item--warhol image_list_card__template-item--warhol-${idx}`)}
            </div>
          ))}
        </div>
      );
    } else if (currentTemplate === "2next") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--left">
            {renderMediaElement()}
          </div>
          <div className="image_list_card__template-item-wrapper image_list_card__template-item-wrapper--right">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "4next") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="image_list_card__template-item-wrapper">
              {renderMediaElement()}
            </div>
          ))}
        </div>
      );
    }
    
    // Fallback to 1up
    return (
      <div className={templateClassName} onClick={maskOpen}>
        {renderMediaElement()}
      </div>
    );
  };

  return (
    <div className="image_list_card__container">
      <div
        style={{}}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={isHovered ? "image_list_card__hover" : "image_list_card"}
      >
        {renderTemplatePreview()}
        {isHovered && (
          <span className={"preview__gif"} onClick={maskOpen}>
            <EyeFilled className={"icon"} /> Preview
          </span>
        )}
        {mask && (
          <ImageMask 
            close={maskClose} 
            url={url} 
            name={name} 
            list={true} 
            mimeType={mimeType}
            width={width}
            height={height}
            template={currentTemplate}
            filter={currentFilter}
          />
        )}
      </div>
      {dimensions && (
        <div className="image_list_card__dimensions">
          {dimensions.orientation} - {dimensions.ratio}
          <br />
          {dimensions.height}px vs {dimensions.width}px
        </div>
      )}
      {(source || giphyId) && (() => {
        const itemSource = source || (giphyId ? 'giphy' : null);
        if (!itemSource) return null;

        // Build search query based on source
        let query;
        if (itemSource === 'giphy') {
          const creator = giphyUsername || (name && name.includes(' by ') ? name.split(' by ').pop().trim() : null);
          query = giphyUsername ? `@${giphyUsername}` : (creator || name);
        } else {
          // For KLIPY/RedGIFs, use the item name as query
          query = name || '';
        }

        const sourceLabel = { giphy: 'Giphy', klipy: 'KLIPY', redgifs: 'RedGIFs' }[itemSource] || itemSource;

        return (
          <div
            className="image_list_card__giphy-user"
            onClick={() => dispatch(searchOpen({ query, source: itemSource }))}
            title={`Search ${sourceLabel} for "${query}"`}
          >
            More like this
          </div>
        );
      })()}
    </div>
  );
};
